/**
 * Feature Flags
 * Central place for all feature toggles
 *
 * Usage:
 *   import { FEATURES } from '@/config/features';
 *   if (FEATURES.USE_XML_POWERPOINT_EDITOR) { ... }
 *
 * Mission Control (formal build):
 *   - Mission Control tabbed layout is the **default** for all role dashboards.
 *   - Legacy infinite canvas: add `?mission=1` to the role route (URL only — not persisted).
 *   - Force Mission Control: `?mission=0`.
 *   - Env `VITE_*_MISSION_CONTROL=false` forces canvas; `true` forces MC.
 */

export const FEATURES = {
  /**
   * Use new OpenXML-based PowerPoint editor
   *
   * When FALSE: Uses existing presentation preview/editor with OpenXML backend for improved rendering
   * When TRUE: Uses new XML-preserving text editor (Phase 1 prototype)
   *
   * CURRENT STATUS: FALSE - Using improved old editor with OpenXML rendering
   *
   * Status: Old editor enhanced with OpenXML backend
   */
  USE_XML_POWERPOINT_EDITOR: false,

  /**
   * Enable formatting controls in PowerPoint editor
   * (Phase 2 feature)
   *
   * Requires: USE_XML_POWERPOINT_EDITOR = true
   */
  ENABLE_POWERPOINT_FORMATTING: false,

  /**
   * Enable image editing in PowerPoint editor
   * (Phase 3 feature)
   *
   * Requires: USE_XML_POWERPOINT_EDITOR = true
   */
  ENABLE_POWERPOINT_IMAGE_EDIT: false,

  /** Mission Control shell (tabs + scroll) — default on in formal build. */
  TEACHER_MISSION_CONTROL: true,

  /** SENCO Mission Control — default on; legacy canvas via `?mission=1`. */
  SENCO_MISSION_CONTROL: true,

  /** Student Mission Control — default on; legacy canvas via `?mission=1`. */
  STUDENT_MISSION_CONTROL: true,

  /** Parent Mission Control — default on; legacy canvas via `?mission=1`. */
  PARENT_MISSION_CONTROL: true,

  /** TA Mission Control — default on; legacy canvas via `?mission=1`. */
  TA_MISSION_CONTROL: true,

  /** Department Head Mission Control — default on; legacy canvas via `?mission=1`. */
  DEPARTMENT_HEAD_MISSION_CONTROL: true,

  /** Senior Staff Mission Control — default on; legacy canvas via `?mission=1`. */
  SENIOR_STAFF_MISSION_CONTROL: true,

  /** Sysadmin Mission Control — default on; legacy canvas via `?mission=1`. */
  SYSADMIN_MISSION_CONTROL: true,

  /** Academy Admin / MAT Mission Control — default on; legacy canvas via `?mission=1`. */
  ACADEMY_ADMIN_MISSION_CONTROL: true,

  /** LEA Admin Mission Control — default on; legacy canvas via `?mission=1`. */
  LEA_ADMIN_MISSION_CONTROL: true,

  /** Gov Admin Mission Control — default on; legacy canvas via `?mission=1`. */
  GOV_ADMIN_MISSION_CONTROL: true,

  /** School Admin Mission Control — default on; legacy canvas via `?mission=1`. */
  SCHOOL_ADMIN_MISSION_CONTROL: true,
};

const MISSION_CONTROL_STORAGE_KEY = 'academy_teacher_mission_control'
const SENCO_MISSION_CONTROL_STORAGE_KEY = 'academy_senco_mission_control'
const STUDENT_MISSION_CONTROL_STORAGE_KEY = 'academy student_mission_control'
const PARENT_MISSION_CONTROL_STORAGE_KEY = 'academy_parent_mission_control'
const TA_MISSION_CONTROL_STORAGE_KEY = 'academy_ta_mission_control'
const DEPARTMENT_HEAD_MISSION_CONTROL_STORAGE_KEY = 'academy_department_head_mission_control'
const SENIOR_STAFF_MISSION_CONTROL_STORAGE_KEY = 'academy_senior_staff_mission_control'
const SYSADMIN_MISSION_CONTROL_STORAGE_KEY = 'academy_sysadmin_mission_control'
const ACADEMY_ADMIN_MISSION_CONTROL_STORAGE_KEY = 'academy_academy_admin_mission_control'
const LEA_ADMIN_MISSION_CONTROL_STORAGE_KEY = 'academy_lea_admin_mission_control'
const GOV_ADMIN_MISSION_CONTROL_STORAGE_KEY = 'academy_gov_admin_mission_control'
const SCHOOL_ADMIN_MISSION_CONTROL_STORAGE_KEY = 'academy_school_admin_mission_control'

/** Bump when layout semantics change — clears stale per-role localStorage once. */
const MISSION_LAYOUT_EPOCH = '2'
const MISSION_LAYOUT_EPOCH_KEY = 'academy_mission_layout_epoch'

const ALL_MISSION_LAYOUT_STORAGE_KEYS = [
  MISSION_CONTROL_STORAGE_KEY,
  SENCO_MISSION_CONTROL_STORAGE_KEY,
  STUDENT_MISSION_CONTROL_STORAGE_KEY,
  PARENT_MISSION_CONTROL_STORAGE_KEY,
  TA_MISSION_CONTROL_STORAGE_KEY,
  DEPARTMENT_HEAD_MISSION_CONTROL_STORAGE_KEY,
  SENIOR_STAFF_MISSION_CONTROL_STORAGE_KEY,
  SYSADMIN_MISSION_CONTROL_STORAGE_KEY,
  ACADEMY_ADMIN_MISSION_CONTROL_STORAGE_KEY,
  LEA_ADMIN_MISSION_CONTROL_STORAGE_KEY,
  GOV_ADMIN_MISSION_CONTROL_STORAGE_KEY,
  SCHOOL_ADMIN_MISSION_CONTROL_STORAGE_KEY,
] as const

let missionLayoutEpochApplied = false

/** Drop PoC-era persisted layout prefs so formal-build defaults (MC on) apply. */
const ensureMissionLayoutEpoch = (): void => {
  if (typeof window === 'undefined' || missionLayoutEpochApplied) return
  missionLayoutEpochApplied = true
  try {
    if (localStorage.getItem(MISSION_LAYOUT_EPOCH_KEY) === MISSION_LAYOUT_EPOCH) return
    for (const key of ALL_MISSION_LAYOUT_STORAGE_KEYS) {
      localStorage.removeItem(key)
    }
    localStorage.setItem(MISSION_LAYOUT_EPOCH_KEY, MISSION_LAYOUT_EPOCH)
  } catch {
    /* ignore */
  }
}

type MissionDashboardRole =
  | 'teacher'
  | 'student'
  | 'senco'
  | 'parent'
  | 'ta'
  | 'department_head'
  | 'senior_staff'
  | 'sysadmin'
  | 'academy_admin'
  | 'lea_admin'
  | 'gov_admin'
  | 'school_admin'

const getMissionDashboardFromPath = (pathname: string): MissionDashboardRole | null => {
  if (pathname.startsWith('/school-admin')) return 'school_admin'
  if (pathname.startsWith('/gov-admin')) return 'gov_admin'
  if (pathname.startsWith('/lea-admin')) return 'lea_admin'
  if (pathname.startsWith('/academy-admin')) return 'academy_admin'
  if (pathname.startsWith('/sysadmin')) return 'sysadmin'
  if (pathname.startsWith('/senior-staff')) return 'senior_staff'
  if (pathname.startsWith('/department-head')) return 'department_head'
  if (pathname.startsWith('/parent')) return 'parent'
  if (pathname.startsWith('/student')) return 'student'
  if (pathname.startsWith('/senco')) return 'senco'
  if (pathname.startsWith('/ta')) return 'ta'
  if (pathname.startsWith('/teacher')) return 'teacher'
  return null
}

const readMissionQuery = (): string | null => {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('mission')
}

const missionQueryMatchesPath = (role: MissionDashboardRole): boolean => {
  if (typeof window === 'undefined') return false
  const pathRole = getMissionDashboardFromPath(window.location.pathname)
  return pathRole === role
}

/**
 * Formal build layout query (inverted from PoC):
 * - `mission=1` → legacy infinite canvas (QA escape hatch, current session URL only)
 * - `mission=0` → Mission Control (explicit)
 *
 * Does not persist to localStorage — avoids getting stuck on canvas after testing `?mission=1`.
 */
const applyMissionLayoutQuery = (
  role: MissionDashboardRole,
  missionValue: string | null
): boolean | null => {
  if (missionValue === '1') {
    if (!missionQueryMatchesPath(role)) return null
    return false
  }
  if (missionValue === '0') {
    if (!missionQueryMatchesPath(role)) return null
    return true
  }
  return null
}

const resolveMissionControlEnabled = (
  role: MissionDashboardRole,
  _storageKey: string,
  envVarName: string,
  featureDefault: boolean
): boolean => {
  ensureMissionLayoutEpoch()

  const envVal = import.meta.env[envVarName]
  if (envVal === 'true') return true
  if (envVal === 'false') return false

  if (typeof window !== 'undefined') {
    const fromQuery = applyMissionLayoutQuery(role, readMissionQuery())
    if (fromQuery !== null) return fromQuery
  }

  return featureDefault
}

/** True when URL is `/student?mission=1` (legacy canvas preview for staff). */
export const isStudentMissionPreviewUrl = (): boolean => {
  if (typeof window === 'undefined') return false
  if (!window.location.pathname.startsWith('/student')) return false
  return readMissionQuery() === '1'
}

/** Allow staff to open the student legacy canvas without a student role. */
export const canAccessStudentMissionPreview = (): boolean => {
  if (!isStudentMissionPreviewUrl()) return false
  return import.meta.env.DEV || import.meta.env.VITE_STUDENT_MISSION_CONTROL === 'false'
}

/** Teacher Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isTeacherMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'teacher',
    MISSION_CONTROL_STORAGE_KEY,
    'VITE_TEACHER_MISSION_CONTROL',
    FEATURES.TEACHER_MISSION_CONTROL
  )

/** SENCO Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isSencoMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'senco',
    SENCO_MISSION_CONTROL_STORAGE_KEY,
    'VITE_SENCO_MISSION_CONTROL',
    FEATURES.SENCO_MISSION_CONTROL
  )

/** Student Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isStudentMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'student',
    STUDENT_MISSION_CONTROL_STORAGE_KEY,
    'VITE_STUDENT_MISSION_CONTROL',
    FEATURES.STUDENT_MISSION_CONTROL
  )

/** Parent Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isParentMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'parent',
    PARENT_MISSION_CONTROL_STORAGE_KEY,
    'VITE_PARENT_MISSION_CONTROL',
    FEATURES.PARENT_MISSION_CONTROL
  )

/** TA Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isTAMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'ta',
    TA_MISSION_CONTROL_STORAGE_KEY,
    'VITE_TA_MISSION_CONTROL',
    FEATURES.TA_MISSION_CONTROL
  )

/** Department Head Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isDepartmentHeadMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'department_head',
    DEPARTMENT_HEAD_MISSION_CONTROL_STORAGE_KEY,
    'VITE_DEPARTMENT_HEAD_MISSION_CONTROL',
    FEATURES.DEPARTMENT_HEAD_MISSION_CONTROL
  )

/** Senior Staff Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isSeniorStaffMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'senior_staff',
    SENIOR_STAFF_MISSION_CONTROL_STORAGE_KEY,
    'VITE_SENIOR_STAFF_MISSION_CONTROL',
    FEATURES.SENIOR_STAFF_MISSION_CONTROL
  )

/** Sysadmin Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isSysAdminMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'sysadmin',
    SYSADMIN_MISSION_CONTROL_STORAGE_KEY,
    'VITE_SYSADMIN_MISSION_CONTROL',
    FEATURES.SYSADMIN_MISSION_CONTROL
  )

/** Academy Admin / MAT Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isAcademyAdminMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'academy_admin',
    ACADEMY_ADMIN_MISSION_CONTROL_STORAGE_KEY,
    'VITE_ACADEMY_ADMIN_MISSION_CONTROL',
    FEATURES.ACADEMY_ADMIN_MISSION_CONTROL
  )

/** LEA Admin Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isLeaAdminMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'lea_admin',
    LEA_ADMIN_MISSION_CONTROL_STORAGE_KEY,
    'VITE_LEA_ADMIN_MISSION_CONTROL',
    FEATURES.LEA_ADMIN_MISSION_CONTROL
  )

/** Gov Admin Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isGovAdminMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'gov_admin',
    GOV_ADMIN_MISSION_CONTROL_STORAGE_KEY,
    'VITE_GOV_ADMIN_MISSION_CONTROL',
    FEATURES.GOV_ADMIN_MISSION_CONTROL
  )

/** School Admin Mission Control shell (tabs + scroll) instead of infinite canvas. */
export const isSchoolAdminMissionControlEnabled = (): boolean =>
  resolveMissionControlEnabled(
    'school_admin',
    SCHOOL_ADMIN_MISSION_CONTROL_STORAGE_KEY,
    'VITE_SCHOOL_ADMIN_MISSION_CONTROL',
    FEATURES.SCHOOL_ADMIN_MISSION_CONTROL
  )

/** Preserve `?mission=` on dashboard redirects (e.g. `/` → `/teacher`). */
export const appendMissionSearch = (pathname: string, search?: string): string => {
  const query =
    search ??
    (typeof window !== 'undefined' ? window.location.search : '')
  if (!query || !query.includes('mission=')) return pathname
  return `${pathname}${query.startsWith('?') ? query : `?${query}`}`
}
